import { getSupabase } from "../auth/supabaseClient";
import {
  currentUtcDateString,
  getDailyLoginReward,
  MIN_CLAIM_GAP_MS,
  type DailyLoginBonusStatus,
  type DailyLoginClaimResult,
  type DailyLoginRewardDef,
  type DailyLoginRewardTier
} from "./dailyLoginRewards";

export interface WalletBalance {
  coins: number;
  gems: number;
}

const WALLET_CACHE_KEY = "bitbeast.wallet.v1";
const DAILY_LOGIN_STATUS_CACHE_KEY = "bitbeast.daily_login_bonus.v1";
const DAILY_LOGIN_DISMISS_MAP_KEY = "bitbeast.daily_login_bonus.dismiss.v1";
const DAILY_CLAIM_TIMESTAMP_KEY = "bitbeast.daily_claim.ts";
const FIRST_WIN_TIMESTAMP_KEY = "bitbeast.first_win_bonus.ts";
const FIRST_WIN_RESET_MS = 24 * 60 * 60 * 1000;

interface DailyLoginDismissMap {
  [userId: string]: string;
}

function loadCachedBalance(): WalletBalance {
  try {
    const raw = localStorage.getItem(WALLET_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WalletBalance>;
      return {
        coins: Math.max(0, Math.floor(parsed.coins ?? 0)),
        gems: Math.max(0, Math.floor(parsed.gems ?? 0))
      };
    }
  } catch {
    // ignore localStorage failures
  }
  return { coins: 0, gems: 0 };
}

function saveCachedBalance(balance: WalletBalance): void {
  try {
    localStorage.setItem(WALLET_CACHE_KEY, JSON.stringify(balance));
  } catch {
    // ignore localStorage failures
  }
}

function normalizeDailyLoginBonusStatus(raw: unknown): DailyLoginBonusStatus | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }

  const candidate = raw as Record<string, unknown>;
  const currentDay = Math.max(0, Math.min(30, Math.floor(Number(candidate.current_day ?? candidate.currentDay ?? 0) || 0)));
  const claimableRaw = candidate.claimable_day ?? candidate.claimableDay;
  const claimableDay = claimableRaw == null
    ? null
    : Math.max(1, Math.min(30, Math.floor(Number(claimableRaw) || 0)));

  return {
    currentDay,
    claimableDay,
    canClaimToday: candidate.can_claim_today === true || candidate.canClaimToday === true,
    lastClaimOn: typeof (candidate.last_claim_on ?? candidate.lastClaimOn) === "string"
      ? String(candidate.last_claim_on ?? candidate.lastClaimOn)
      : null,
    todayUtc: typeof (candidate.today_utc ?? candidate.todayUtc) === "string"
      ? String(candidate.today_utc ?? candidate.todayUtc)
      : currentUtcDateString()
  };
}

function loadCachedDailyLoginStatus(): DailyLoginBonusStatus | null {
  try {
    const raw = localStorage.getItem(DAILY_LOGIN_STATUS_CACHE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeDailyLoginBonusStatus(JSON.parse(raw));
  } catch {
    return null;
  }
}

function saveCachedDailyLoginStatus(status: DailyLoginBonusStatus | null): void {
  try {
    if (status == null) {
      localStorage.removeItem(DAILY_LOGIN_STATUS_CACHE_KEY);
      return;
    }
    localStorage.setItem(DAILY_LOGIN_STATUS_CACHE_KEY, JSON.stringify(status));
  } catch {
    // ignore localStorage failures
  }
}

function loadDailyLoginDismissMap(): DailyLoginDismissMap {
  try {
    const raw = localStorage.getItem(DAILY_LOGIN_DISMISS_MAP_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed != null && typeof parsed === "object"
      ? parsed as DailyLoginDismissMap
      : {};
  } catch {
    return {};
  }
}

function saveDailyLoginDismissMap(map: DailyLoginDismissMap): void {
  try {
    localStorage.setItem(DAILY_LOGIN_DISMISS_MAP_KEY, JSON.stringify(map));
  } catch {
    // ignore localStorage failures
  }
}

/**
 * Retry a function with exponential backoff.
 * Used for critical economy RPCs where a single network glitch shouldn't lose rewards.
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelayMs = 500,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, baseDelayMs * Math.pow(2, attempt)));
      }
    }
  }
  throw lastErr;
}

let cachedBalance: WalletBalance = loadCachedBalance();
let cachedDailyLoginStatus: DailyLoginBonusStatus | null = loadCachedDailyLoginStatus();
let inMemoryFirstWinBonusTs = 0;
let inMemoryLastClaimTs = 0;

function getLastDailyClaimTimestamp(): number {
  try {
    const raw = localStorage.getItem(DAILY_CLAIM_TIMESTAMP_KEY);
    if (raw != null) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) {
        inMemoryLastClaimTs = parsed;
        return parsed;
      }
    }
  } catch {
    // ignore and use in-memory fallback
  }
  return inMemoryLastClaimTs;
}

function setLastDailyClaimTimestamp(ts: number): void {
  inMemoryLastClaimTs = Math.max(0, Math.floor(ts));
  try {
    localStorage.setItem(DAILY_CLAIM_TIMESTAMP_KEY, String(inMemoryLastClaimTs));
  } catch {
    // ignore localStorage failures
  }
}

export function resetWalletCache(): void {
  cachedBalance = { coins: 0, gems: 0 };
  cachedDailyLoginStatus = null;
  try {
    localStorage.removeItem(WALLET_CACHE_KEY);
    localStorage.removeItem(DAILY_LOGIN_STATUS_CACHE_KEY);
    localStorage.removeItem(DAILY_LOGIN_DISMISS_MAP_KEY);
    localStorage.removeItem(DAILY_CLAIM_TIMESTAMP_KEY);
    localStorage.removeItem(FIRST_WIN_TIMESTAMP_KEY);
  } catch {
    // ignore localStorage failures
  }
  inMemoryFirstWinBonusTs = 0;
  inMemoryLastClaimTs = 0;
}

export function getCachedBalance(): WalletBalance {
  return { ...cachedBalance };
}

export function getCachedDailyLoginBonusStatus(): DailyLoginBonusStatus | null {
  return cachedDailyLoginStatus == null ? null : { ...cachedDailyLoginStatus };
}

export function shouldAutoOpenDailyLoginBonus(userId: string | null | undefined, status: DailyLoginBonusStatus | null): boolean {
  if (userId == null || userId === "guest-local" || status == null || !status.canClaimToday) {
    return false;
  }
  const dismissMap = loadDailyLoginDismissMap();
  return dismissMap[userId] !== status.todayUtc;
}

export function markDailyLoginBonusDismissed(userId: string | null | undefined, utcDate: string): void {
  if (userId == null || userId === "guest-local" || !utcDate) {
    return;
  }
  const dismissMap = loadDailyLoginDismissMap();
  dismissMap[userId] = utcDate;
  saveDailyLoginDismissMap(dismissMap);
}

export function clearDailyLoginBonusDismissal(userId: string | null | undefined): void {
  if (userId == null || userId === "guest-local") {
    return;
  }
  const dismissMap = loadDailyLoginDismissMap();
  if (dismissMap[userId] == null) {
    return;
  }
  delete dismissMap[userId];
  saveDailyLoginDismissMap(dismissMap);
}

export async function fetchBalance(userId: string): Promise<WalletBalance> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("user_wallets")
    .select("coins, gems")
    .eq("user_id", userId)
    .single();

  if (error || data == null) {
    console.warn("[wallet] fetchBalance failed:", error?.message ?? "no data");
    return cachedBalance;
  }

  cachedBalance = { coins: data.coins ?? 0, gems: data.gems ?? 0 };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export async function creditCoins(
  userId: string,
  amount: number,
  reason: string,
  referenceId?: string
): Promise<WalletBalance | null> {
  if (amount <= 0) return null;

  try {
    const result = await retryWithBackoff(async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase.rpc("credit_coins", {
        p_user_id: userId,
        p_amount: amount,
        p_reason: reason,
        p_reference_id: referenceId ?? null
      });
      if (error) throw new Error(error.message);
      if (!data?.ok) throw new Error(data?.error ?? "credit_coins failed");
      return data;
    });

    cachedBalance = { coins: result.coins, gems: cachedBalance.gems };
    saveCachedBalance(cachedBalance);
    return cachedBalance;
  } catch (err) {
    console.warn("[wallet] creditCoins failed after retries:", err instanceof Error ? err.message : err);
    return null;
  }
}

export async function exchangeCoinsToGems(
  userId: string,
  coinAmount: number
): Promise<WalletBalance | null> {
  if (coinAmount < 100 || coinAmount % 100 !== 0) return null;

  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("exchange_coins_to_gems", {
    p_user_id: userId,
    p_coin_amount: coinAmount
  });

  if (error || !data?.ok) {
    console.warn("[wallet] exchange failed:", error?.message ?? data?.error);
    return null;
  }

  cachedBalance = { coins: data.coins, gems: data.gems };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export async function creditGems(
  userId: string,
  amount: number,
  reason: string
): Promise<WalletBalance | null> {
  if (amount <= 0) return null;

  try {
    const result = await retryWithBackoff(async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase.rpc("credit_gems", {
        p_user_id: userId,
        p_amount: amount,
        p_reason: reason
      });
      if (error) throw new Error(error.message);
      if (!data?.ok) throw new Error(data?.error ?? "credit_gems failed");
      return data;
    });

    cachedBalance = { coins: cachedBalance.coins, gems: result.gems };
    saveCachedBalance(cachedBalance);
    return cachedBalance;
  } catch (err) {
    console.warn("[wallet] creditGems failed after retries:", err instanceof Error ? err.message : err);
    return null;
  }
}

export function creditGemsLocal(amount: number): WalletBalance {
  if (amount <= 0) return cachedBalance;
  cachedBalance = { coins: cachedBalance.coins, gems: cachedBalance.gems + amount };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

/** Deduct gems locally (for guest/offline). Returns null if insufficient funds. */
export function deductGemsLocal(amount: number): WalletBalance | null {
  if (amount <= 0) return cachedBalance;
  if (cachedBalance.gems < amount) return null;
  cachedBalance = { coins: cachedBalance.coins, gems: cachedBalance.gems - amount };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export function creditCoinsLocal(amount: number): WalletBalance {
  if (amount <= 0) return cachedBalance;
  cachedBalance = { coins: cachedBalance.coins + amount, gems: cachedBalance.gems };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export function exchangeCoinsToGemsLocal(coinAmount: number): WalletBalance | null {
  if (coinAmount < 100 || coinAmount % 100 !== 0) return null;
  if (cachedBalance.coins < coinAmount) return null;
  const gemGain = coinAmount / 100;
  cachedBalance = {
    coins: cachedBalance.coins - coinAmount,
    gems: cachedBalance.gems + gemGain
  };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export function scoreToCoins(totalScore: number): number {
  return Math.floor(totalScore / 100);
}

export function versusCoins(score: number, isWinner: boolean): number {
  return Math.floor(score / (isWinner ? 100 : 200));
}

export function buyGemPackLocal(coinCost: number, gemAmount: number): WalletBalance | null {
  if (cachedBalance.coins < coinCost) return null;
  cachedBalance = {
    coins: cachedBalance.coins - coinCost,
    gems: cachedBalance.gems + gemAmount
  };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export async function buyGemPack(
  userId: string,
  coinCost: number,
  gemAmount: number
): Promise<WalletBalance | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("buy_gem_pack", {
    p_user_id: userId,
    p_coin_amount: coinCost,
    p_gem_amount: gemAmount
  });

  if (error || !data?.ok) {
    console.warn("[wallet] buyGemPack failed:", error?.message ?? data?.error);
    return null;
  }

  cachedBalance = { coins: data.coins, gems: data.gems };
  saveCachedBalance(cachedBalance);
  return cachedBalance;
}

export const RETENTION_REWARD_TABLE = {
  versusWin: { gems: 10 },
  versusLoss: { gems: 0 },
  firstWinOfDayBonus: { gems: 5 }
} as const;

function rewardFromClaimPayload(raw: Record<string, unknown>, status: DailyLoginBonusStatus): DailyLoginRewardDef {
  const rewardDay = Math.max(1, Math.min(30, Math.floor(Number(raw.reward_day ?? status.currentDay) || status.currentDay || 1)));
  const knownReward = getDailyLoginReward(rewardDay);
  if (knownReward != null) {
    return knownReward;
  }
  const rewardTierRaw = raw.reward_tier;
  const rewardTier: DailyLoginRewardTier = rewardTierRaw === "bigger" || rewardTierRaw === "substantial"
    ? rewardTierRaw
    : "normal";
  return {
    day: rewardDay,
    coins: Math.max(0, Math.floor(Number(raw.reward_coins) || 0)),
    gems: Math.max(0, Math.floor(Number(raw.reward_gems) || 0)),
    tier: rewardTier
  };
}

export async function fetchDailyLoginBonusStatus(userId: string): Promise<DailyLoginBonusStatus | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("get_daily_login_bonus_status", {
    p_user_id: userId
  });

  if (error || !data?.ok) {
    console.warn("[wallet] fetchDailyLoginBonusStatus failed:", error?.message ?? data?.error);
    return cachedDailyLoginStatus;
  }

  const status = normalizeDailyLoginBonusStatus(data);
  if (status == null) {
    console.warn("[wallet] fetchDailyLoginBonusStatus failed: invalid payload");
    return cachedDailyLoginStatus;
  }

  cachedDailyLoginStatus = status;
  saveCachedDailyLoginStatus(status);
  return status;
}

export async function claimDailyLoginBonus(userId: string, nowMs = Date.now()): Promise<DailyLoginClaimResult | null> {
  // Enforce minimum 20-hour gap between claims to prevent timezone exploits (#144)
  const lastClaimTs = getLastDailyClaimTimestamp();
  if (lastClaimTs > 0 && nowMs - lastClaimTs < MIN_CLAIM_GAP_MS) {
    console.warn("[wallet] claimDailyLoginBonus blocked: less than 20h since last claim");
    return null;
  }

  let data: Record<string, unknown>;
  try {
    data = await retryWithBackoff(async () => {
      const supabase = getSupabase();
      const { data: d, error } = await supabase.rpc("claim_daily_login_bonus", {
        p_user_id: userId
      });
      if (error) throw new Error(error.message);
      if (!d?.ok) throw new Error(d?.error ?? "claim_daily_login_bonus failed");
      return d as Record<string, unknown>;
    });
  } catch (err) {
    console.warn("[wallet] claimDailyLoginBonus failed after retries:", err instanceof Error ? err.message : err);
    return null;
  }

  const status = normalizeDailyLoginBonusStatus(data);
  if (status == null) {
    console.warn("[wallet] claimDailyLoginBonus failed: invalid payload");
    return null;
  }

  cachedBalance = {
    coins: Math.max(0, Math.floor(Number(data.coins) || 0)),
    gems: Math.max(0, Math.floor(Number(data.gems) || 0))
  };
  cachedDailyLoginStatus = status;
  saveCachedBalance(cachedBalance);
  saveCachedDailyLoginStatus(status);
  setLastDailyClaimTimestamp(nowMs);

  return {
    balance: { ...cachedBalance },
    status,
    reward: rewardFromClaimPayload(data as Record<string, unknown>, status)
  };
}

function getLastFirstWinBonusTime(): number {
  try {
    const raw = localStorage.getItem(FIRST_WIN_TIMESTAMP_KEY);
    if (raw != null) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) {
        inMemoryFirstWinBonusTs = parsed;
        return parsed;
      }
    }
  } catch {
    // ignore and use in-memory fallback
  }
  return inMemoryFirstWinBonusTs;
}

function setLastFirstWinBonusTime(ts: number): void {
  inMemoryFirstWinBonusTs = Math.max(0, Math.floor(ts));
  try {
    localStorage.setItem(FIRST_WIN_TIMESTAMP_KEY, String(inMemoryFirstWinBonusTs));
  } catch {
    // ignore localStorage failures
  }
}

export function canClaimFirstWinBonus(nowMs = Date.now()): boolean {
  return nowMs - getLastFirstWinBonusTime() >= FIRST_WIN_RESET_MS;
}

export function claimFirstWinBonusLocal(nowMs = Date.now()): WalletBalance | null {
  if (!canClaimFirstWinBonus(nowMs)) {
    return null;
  }
  setLastFirstWinBonusTime(nowMs);
  return creditGemsLocal(RETENTION_REWARD_TABLE.firstWinOfDayBonus.gems);
}

export async function claimFirstWinBonus(userId: string, nowMs = Date.now()): Promise<WalletBalance | null> {
  if (!canClaimFirstWinBonus(nowMs)) {
    return null;
  }
  setLastFirstWinBonusTime(nowMs);
  const result = await creditGems(userId, RETENTION_REWARD_TABLE.firstWinOfDayBonus.gems, "first_win_bonus");
  if (result != null) {
    return result;
  }
  return creditGemsLocal(RETENTION_REWARD_TABLE.firstWinOfDayBonus.gems);
}

export const FIRST_WIN_RESET_WINDOW_MS = FIRST_WIN_RESET_MS;
